import React, { useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { MapPin, Mail, Phone, Clock } from "lucide-react";

export default function Contact() {
  const [f, setF] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/contact", f);
      toast.success("Message sent — we'll be in touch shortly.");
      setF({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to send");
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-[#040B16] text-white">
      <div className="max-w-7xl mx-auto px-6 py-24 grid lg:grid-cols-2 gap-14">
        <div>
          <div className="eyebrow mb-3">Get in touch</div>
          <h1 className="font-display font-extrabold text-5xl tracking-tighter mb-6">We'd love to hear from you.</h1>
          <p className="text-white/70 mb-10 leading-relaxed">
            Whether you're planning your next trip or exploring a corporate program, our concierge team is a phone call away.
          </p>
          <div className="space-y-5 mb-10">
            <Item icon={MapPin} label="Head office" text="500 North Central Expressway, Suite 500, Plano, TX 75074 US" />
            <Item icon={Phone} label="Phone" text="+1 (682) 449-4920" />
            <Item icon={Mail} label="Email" text="info@globalprimefleet.com" />
            <Item icon={Clock} label="Business hours" text="Monday — Friday · 8:30 am — 5:00 pm" />
          </div>
          <div className="rounded-2xl overflow-hidden border border-white/10">
            <iframe
              title="map"
              src="https://www.google.com/maps?q=500+N+Central+Expy,+Plano,+TX+75074&output=embed"
              className="w-full h-72 border-0" loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
          </div>
        </div>
        <form onSubmit={submit} className="card-marketing p-8 space-y-4 h-fit">
          <Field label="Name" value={f.name} onChange={(v) => setF({ ...f, name: v })} testId="contact-name" />
          <Field label="Email" type="email" value={f.email} onChange={(v) => setF({ ...f, email: v })} testId="contact-email" />
          <Field label="Phone (optional)" value={f.phone} onChange={(v) => setF({ ...f, phone: v })} testId="contact-phone" required={false} />
          <label className="block">
            <span className="text-[10px] uppercase tracking-[0.2em] text-white/60">Message</span>
            <textarea data-testid="contact-message" rows="5" value={f.message} required
              onChange={(e) => setF({ ...f, message: e.target.value })}
              className="mt-2 w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37] text-sm" />
          </label>
          <button data-testid="contact-submit" className="btn-gold w-full justify-center" disabled={busy}>
            {busy ? "Sending…" : "Send message"}
          </button>
        </form>
      </div>
    </div>
  );
}
function Item({ icon: Icon, label, text }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-full border border-white/15 grid place-items-center shrink-0">
        <Icon className="w-4 h-4 text-[#D4AF37]" />
      </div>
      <div>
        <div className="text-[10px] uppercase tracking-widest text-white/50">{label}</div>
        <div className="text-white/90">{text}</div>
      </div>
    </div>
  );
}
function Field({ label, value, onChange, type = "text", testId, required = true }) {
  return (
    <label className="block">
      <span className="text-[10px] uppercase tracking-[0.2em] text-white/60">{label}</span>
      <input type={type} value={value} required={required} data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        className="mt-2 w-full bg-transparent border border-white/10 rounded-lg px-3 py-2.5 outline-none focus:border-[#D4AF37] text-sm" />
    </label>
  );
}
